import React from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';

// ── Suggested next actions ───────────────────────────────────────────────────
// Follow-up CTA rendered under the LAST bot message of a completed search /
// clarification-answer turn. The action list itself is built in BotMessage
// from the turn's data; this component is purely presentational.
//
// `prompts` is the primary mode: the backend ships one short follow-up
// question per line in `callToAction`, and each renders as its own tappable
// bubble (stacked) — clicking one sends that question as the user's reply.
// The bubbles reuse the chat's own bubble classes and body type so they read
// as part of the conversation, not as a foreign widget.
//
// Legacy modes kept below: `onLeadClick` (single lead bubble) and the passive
// panel with chips.

export interface SuggestedAction {
  key: string;
  label: string;
  onClick: () => void;
}

export interface SuggestedPrompt {
  text: string;
  onClick: () => void;
}

interface SuggestedActionsProps {
  leadText?: string | null;
  actions: SuggestedAction[];
  disabled?: boolean;
  /** When set, the lead sentence renders as a clickable prompt pill that
   *  fires this handler (sends the follow-up as the user's reply). */
  onLeadClick?: () => void;
  /** One clickable follow-up question per pill, stacked vertically. Takes
   *  precedence over leadText/onLeadClick when non-empty. */
  prompts?: SuggestedPrompt[];
}

// Each question renders as a bot-bubble-shaped button: the SAME classes the
// chat bubbles use (p-2 sm:p-3 rounded-2xl shadow-sm bg-white) and the same
// body type (14px, #333, 1.55) so the pills read as part of the conversation.
// The indigo sparkle/arrow are the only clickability affordances.
const PromptBubble: React.FC<{ text: string; onClick: () => void; disabled?: boolean }> = ({ text, onClick, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className="flex items-center gap-2 p-2 sm:p-3 rounded-2xl shadow-sm bg-white text-left transition-colors hover:bg-[#f7f7ff] disabled:opacity-50 disabled:cursor-default"
    style={{ fontSize: '14px', lineHeight: '1.55', color: '#333', fontFamily: 'inherit' }}
  >
    <Sparkles className="flex-shrink-0" style={{ width: 14, height: 14, color: '#6866ff' }} />
    <span>{text}</span>
    <ArrowRight className="flex-shrink-0" style={{ width: 14, height: 14, color: '#6866ff' }} />
  </button>
);

const SuggestedActions: React.FC<SuggestedActionsProps> = ({ leadText, actions, disabled, onLeadClick, prompts }) => {
  const lead = (leadText || '').trim();
  const promptList = (prompts || []).filter((p) => p.text && p.text.trim());
  if (!actions.length && !lead && !promptList.length) return null;

  // ── One bubble-styled pill per follow-up question, stacked ────────────────
  // The empty leading div mirrors the avatar column (w-8 sm:w-10 + gap-2
  // sm:gap-3) so the pills left-align with the bot bubbles above them.
  if (promptList.length) {
    return (
      <div className="flex gap-2 sm:gap-3 flex-row mt-2 sm:mt-3">
        <div className="w-8 sm:w-10 flex-shrink-0" />
        <div className="flex flex-col items-start gap-2 min-w-0 sm:max-w-[34rem] lg:max-w-[44rem] xl:max-w-[48rem]">
          {promptList.map((p) => (
            <PromptBubble key={p.text} text={p.text.trim()} onClick={p.onClick} disabled={disabled} />
          ))}
        </div>
      </div>
    );
  }

  // ── Clickable prompt pill (no chips case) ─────────────────────────────────
  if (lead && onLeadClick && actions.length === 0) {
    return (
      <div className="flex gap-2 sm:gap-3 flex-row mt-2 sm:mt-3">
        <div className="w-8 sm:w-10 flex-shrink-0" />
        <div className="flex flex-col items-start min-w-0 sm:max-w-[34rem] lg:max-w-[44rem] xl:max-w-[48rem]">
          <PromptBubble text={lead} onClick={onLeadClick} disabled={disabled} />
        </div>
      </div>
    );
  }

  // ── Passive panel (lead text and/or chips) ────────────────────────────────
  return (
    <div
      style={{
        margin: '10px 0 2px 44px',
        maxWidth: '30rem',
        backgroundColor: '#f7f7ff',
        border: '1px solid #e6e4ff',
        borderRadius: 14,
        padding: '10px 12px',
      }}
    >
      {lead && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: actions.length ? 9 : 0 }}>
          <span
            style={{
              flexShrink: 0,
              width: 22,
              height: 22,
              borderRadius: 7,
              backgroundColor: '#ececff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Sparkles style={{ width: 12, height: 12, color: '#6866ff' }} />
          </span>
          {/* Quiet hint: small, regular weight, muted — the lead recedes behind
              the chips, which carry the action. */}
          <span style={{ fontSize: 12, lineHeight: '17px', color: '#6b6884', fontWeight: 400, paddingTop: 3 }}>
            {lead}
          </span>
        </div>
      )}
      {actions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {actions.map((a) => (
            <button
              key={a.key}
              type="button"
              disabled={disabled}
              onClick={a.onClick}
              className="suggested-action-chip"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '5px 11px',
                fontSize: 12.5,
                lineHeight: '17px',
                fontFamily: 'inherit',
                fontWeight: 500,
                color: '#4a48b8',
                backgroundColor: '#ffffff',
                border: '1px solid #dcdaff',
                borderRadius: 999,
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.55 : 1,
                transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              onMouseEnter={(e) => {
                if (disabled) return;
                e.currentTarget.style.backgroundColor = '#6866ff';
                e.currentTarget.style.borderColor = '#6866ff';
                e.currentTarget.style.color = '#ffffff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.borderColor = '#dcdaff';
                e.currentTarget.style.color = '#4a48b8';
              }}
            >
              {a.label}
              <ArrowRight style={{ width: 11, height: 11, flexShrink: 0 }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SuggestedActions;
